import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  Camera,
  BookOpen,
  PoundSterling,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/ai-quoting-software";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | AI Quoting Software for UK Builders",
  "description": "AI quoting software that turns your drawings, photos or a description into a fully-priced quote in under 10 minutes. Built for UK builders.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "AI Quoting Software", "item": PAGE_URL },
  ],
};

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "AI Quoting Software for UK Builders | The Proper Tool",
  "description": "AI quoting software that turns your drawings, photos or a description into a fully-priced quote in under 10 minutes. Built for UK builders. Free 14-day trial.",
  "url": PAGE_URL,
  "breadcrumb": BREADCRUMB_SCHEMA,
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", "h2"],
  },
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is AI quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "AI quoting software uses artificial intelligence to generate a fully-priced construction quote from your project inputs: drawings, photos, or a description. The AI identifies every trade, calculates quantities, and applies real market rates in minutes. Purpose-built tools like The Proper Tool use UK construction rates, so the output is a review-ready, itemised quote, not a rough estimate.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there an AI quoting app for builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is an AI quoting app built specifically for UK builders. It runs in any browser, desktop or mobile, and generates a fully-priced, itemised quote from your drawings, photos, or a job description. Plans start from £29/month with a 14-day free trial and no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "How does AI create a construction quote?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool's AI reads your project inputs: drawings, photos, or a plain-text description. It identifies the scope (trades, quantities, and build sequence) and prices each line item using a 519-item UK cost library covering 31 trade categories. The result is a fully itemised quote you can review, edit, and export as a branded PDF.",
      },
    },
    {
      "@type": "Question",
      "name": "How accurate is AI quoting?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Accuracy depends on input quality and rate calibration. The Proper Tool improves accuracy three ways: it uses purpose-built UK construction rates; its Pricing Accuracy Index learns from your accepted quotes over time; and a built-in validation pipeline flags pricing outliers and scope gaps before you send the quote to a client.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool free to try?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What is AI quoting software?",
    a: "AI quoting software uses artificial intelligence to generate a fully-priced construction quote from your project inputs: drawings, photos, or a description. The AI identifies every trade, calculates quantities, and applies real market rates in minutes. Purpose-built tools like The Proper Tool use UK construction rates, so the output is a review-ready, itemised quote, not a rough estimate.",
  },
  {
    q: "Is there an AI quoting app for builders?",
    a: "Yes. The Proper Tool is an AI quoting app built specifically for UK builders. It runs in any browser, desktop or mobile, and generates a fully-priced, itemised quote from your drawings, photos, or a job description. Plans start from £29/month with a 14-day free trial and no credit card required.",
  },
  {
    q: "How does AI create a construction quote?",
    a: "The Proper Tool's AI reads your project inputs: drawings, photos, or a plain-text description. It identifies the scope (trades, quantities, and build sequence) and prices each line item using a 519-item UK cost library covering 31 trade categories. The result is a fully itemised quote you can review, edit, and export as a branded PDF.",
  },
  {
    q: "How accurate is AI quoting?",
    a: "Accuracy depends on input quality and rate calibration. The Proper Tool improves accuracy three ways: it uses purpose-built UK construction rates; its Pricing Accuracy Index learns from your accepted quotes over time; and a built-in validation pipeline flags pricing outliers and scope gaps before you send the quote to a client.",
  },
  {
    q: "Is The Proper Tool free to try?",
    a: "Yes. The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start. After the trial, plans start from £29/month for sole traders.",
  },
];

const RELATED_PAGES = [
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/ai-takeoff-software/", label: "AI Takeoff Software" },
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/ai-construction-estimating-software/", label: "AI Construction Estimating Software" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function AiQuotingSoftwarePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="AI Quoting Software for UK Builders | The Proper Tool"
        description="AI quoting software that turns your drawings, photos or a description into a fully-priced quote in under 10 minutes. Built for UK builders. Free 14-day trial."
        canonical="/ai-quoting-software"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, WEBPAGE_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>AI Quoting Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              AI Quoting Software Built for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Meet Smart Quoting. Upload your drawings or photos, or just describe the job, and get a fully-priced, professional quote in under 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
          </div>
        </section>

        {/* What is AI quoting software */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What is AI quoting software?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              AI quoting software uses artificial intelligence to generate a fully-priced, itemised construction quote from your project inputs: drawings, photos, or a plain-text description. Instead of building a scope line by line, you hand the job to the AI and review what comes back.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Generic AI tools produce rough estimates. The Proper Tool is purpose-built: its AI is trained on UK construction work, applies real trade rates, and covers all 31 common residential trade categories. Every quote is itemised, editable, and exports as a branded PDF.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The result is a professional quote your client expects, produced in under 10 minutes, not hours.
            </p>
          </div>
        </section>

        {/* Modality hub grid */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="smart-quoting-modalities">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Five ways to generate a quote with AI</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Work from whatever you have on site or in the office. Upload your drawings, photos or a quick description (by typing or voice), and The Proper Tool's AI builds a fully-priced, professional quote in under 10 minutes.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Zap,
                  title: "Auto Quote",
                  description: "Describe the job in plain English. The AI turns your words into a fully itemised scope of works with quantities and UK rates applied to every line.",
                  href: "/auto-quote",
                  label: "Explore Auto Quote",
                },
                {
                  icon: FileText,
                  title: "AI Takeoff from Drawings",
                  description: "Upload PDFs of architect's plans or structural drawings. The AI extracts beam sizes, floor areas, spans, and connection details to price the job accurately.",
                  href: "/ai-takeoff-software",
                  label: "Explore AI Takeoff Software",
                },
                {
                  icon: Camera,
                  title: "Quote from a Photo",
                  description: "Attach site photos and the AI identifies scope from what it sees: existing conditions, materials, and build complexity, then prices each element.",
                  href: "/quote-from-photo",
                  label: "Explore Quote from Photo",
                },
                {
                  icon: FileText,
                  title: "Quote from Drawings",
                  description: "Turn architectural drawings and structural plans into a priced bill of quantities. The AI reads your PDFs and generates an itemised scope ready to review.",
                  href: "/quote-from-drawings",
                  label: "Explore Quote from Drawings",
                },
                {
                  icon: BookOpen,
                  title: "Quote from Description",
                  description: "Type or paste a project description (site notes, a spec document, or a plain-language brief) and get a fully-priced, itemised quote in minutes.",
                  href: "/quote-from-description",
                  label: "Explore Quote from Description",
                },
                {
                  icon: PoundSterling,
                  title: "AI Pricing Intelligence",
                  description: "The Pricing Accuracy Index learns your historical rates and self-calibrates every future quote. AI that gets more accurate with every job you complete.",
                  href: "/ai-quoting-software#pricing-intelligence",
                  label: "How pricing intelligence works",
                },
              ].map((modality) => (
                <div key={modality.title} className="bg-background rounded-xl border border-border p-6">
                  <modality.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{modality.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{modality.description}</p>
                  <Link href={modality.href} className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" />
                    {modality.label}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Under 10 minutes */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">A fully-priced quote in under 10 minutes</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Traditional quoting takes hours: measure, research rates, build a spreadsheet, format a PDF. Smart Quoting compresses that into one step. Describe the job, upload your inputs, and get back a fully itemised scope with quantities and rates applied, typically in under 10 minutes.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Every trade covered: groundworks through to decoration",
                "Quantities calculated, not estimated",
                "Unit rates from the UK construction cost library",
                "Editable line by line before you send",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Link href="/auto-quote/" className="text-primary font-medium hover:underline flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
              Learn about Auto Quote
            </Link>
          </div>
        </section>

        {/* Built on real UK rates */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Built on real UK rates</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Generic AI tools pull from the internet and guess. The Proper Tool prices every quote from a 519-item cost library covering 31 trade categories, built for UK residential construction, with Low, Typical, and High rate bands built in from day one.
            </p>
            <ul className="space-y-3">
              {[
                "519 rated tasks across 31 trade categories",
                "Low, Typical, and High rate bands",
                "Editable to your own rates: override any line",
                "Regional pricing adjustments available on Starter and Pro",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing Accuracy Index */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing that learns how you charge</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Every builder prices differently: different margins, different markets, different ways of working. The Pricing Accuracy Index tracks which quotes you accept, which you adjust, and how, then feeds those signals back into future quotes so they get closer to your numbers over time.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              This is not generic AI. It is AI that calibrates to your business.
            </p>
            <Link href="/ai-construction-estimating-software/" className="text-primary font-medium hover:underline flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
              AI construction estimating software
            </Link>
          </div>
        </section>

        {/* Differentiation */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">An AI quoting app for builders, not generic software</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Spreadsheets are slow. Generic SaaS tools are built for other markets. The Proper Tool is built around one thing: helping UK builders produce accurate, professional quotes faster than any other method.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Zap, label: "No spreadsheet setup: start quoting immediately" },
                { icon: FileText, label: "Reads structural drawings, not just text descriptions" },
                { icon: CheckCircle2, label: "Built-in validation flags scope gaps and pricing outliers" },
                { icon: PoundSterling, label: "UK rates, UK trades, UK residential construction" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 bg-background rounded-lg border border-border p-4">
                  <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing callout */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple pricing, free to try</h2>
              <p className="text-lg text-muted-foreground">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 519-item cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Accuracy Index", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo + VAT</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-center space-x-4 text-sm">
              <Link href="/pricing/" className="text-primary font-medium hover:underline">Full pricing details</Link>
              <span className="text-muted-foreground">·</span>
              <Link href="/free-trial/" className="text-primary font-medium hover:underline">About the free trial</Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
